import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { Checkbox, IconButton, MenuItem, Select } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  renderTextEditor,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';


function BalanceSheet({
  name,
  rows,
  companies,
  organisations,
  dates,
  activeFilter,
  chartofaccounts,
  selectedDates,
  orgLength,
  onClickedAdd,
  onClickedDelete,
  getStateData,
  resetReportingState,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    ClientName: '',
    DimDateKey: 'All',
    DimChartOfAccountsKey: 'All',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    BalanceSheetCategory: '',
    OpeningBalance: '',
    ClosingBalance: '',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'DimDateKey':
      case 'OpeningBalance':
      case 'ClosingBalance':
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };
  
      case 'BalanceSheetCategory':
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };

      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimChartOfAccountsKey':
        return (a, b) => {
          const aName = getChartOfAccountsNameById(a[sortColumn]);
          const bName = getChartOfAccountsNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    // Normalize props to safe arrays
    const safeRows = Array.isArray(rows) ? [...rows] : [];
    const safeCoA  = Array.isArray(chartofaccounts) ? chartofaccounts : [];

    safeRows.forEach((data) => {
      const hit = safeCoA.find((acc) => acc[0] === data.DimChartOfAccountsKey);
      data.DimAccountName = hit ? hit[1] : '';
    });

    // Sort
    safeRows.sort((a, b) =>
      b.DimDateKey === a.DimDateKey
        ? (a.DimAccountName < b.DimAccountName ? 1 : -1)
        : b.DimDateKey - a.DimDateKey
    );

    // Apply Active filter
    if (activeFilter) {
      setTableRows(safeRows.filter((row) => row.IsActive === true));
    } else {
      setTableRows(safeRows);
    }
  }, [rows, chartofaccounts, activeFilter]);


  const getChartOfAccountsNameById = (id) => {
    if (!id) return '';
    const account = chartofaccounts?.find(account => account[0] === id);
    return account ? account[1] : '';
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'DimDateKey',
      'DimChartOfAccountsKey',
      'IsActive',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();

    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    // check import data is valid
    const validData = updated_data.every(item => {
      return item.hasOwnProperty('DimChartOfAccountsKey') && item.hasOwnProperty('BalanceSheetCategory')
        && item.hasOwnProperty('OpeningBalance') && item.hasOwnProperty('ClosingBalance');
    });
    if (!validData) {
      toast.warning('Please insert the valid data. Data must contain DimChartOfAccountsKey, BalanceSheetCategory, OpeningBalance, and ClosingBalance.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
    ).then((res) => {
      if (res.status === 200) {
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (
      field.column.key === 'OpeningBalance' ||
      field.column.key === 'ClosingBalance'
    ) {
      if (isNaN(updated_data.updated[field.column.key])) {
        toast.warning('Please insert the number value.');
        return;
      }
    }

    if (field.column.key === 'DimChartOfAccountsKey') {
      updated_data.updated['BalanceSheetCategory'] = chartofaccounts.find(
        (acc) => acc[0] === updated_data.updated[field.column.key],
      )
        ? chartofaccounts.find(
            (acc) => acc[0] === updated_data.updated[field.column.key],
          )[4]
        : null;

      updatedRows[field.indexes[0]]['BalanceSheetCategory'] =
        updated_data.updated['BalanceSheetCategory'];
    }

    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];      
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id)) {
        row[field.column.key] =
          updatedRows[field.indexes[0]][field.column.key];

        if (field.column.key === 'DimChartOfAccountsKey') {
          row['BalanceSheetCategory'] = chartofaccounts.find(
            (acc) =>
              acc[0] === updatedRows[field.indexes[0]][field.column.key],
          )
            ? chartofaccounts.find(
                (acc) =>
                  acc[0] === updatedRows[field.indexes[0]][field.column.key],
              )[4]
            : null;
        }
      }
      return row;
    });

    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const bsChartOfAccounts = useMemo(() => {
    let bs_accounts = chartofaccounts.filter((account) => account[6] === 'BS' && account[2] === true);

    return bs_accounts;
  }, [chartofaccounts]);
  
  const balanceSheetColumns = useMemo(() => {
    let balancesheet_columns = [
      SelectColumn,
      {
        key: 'DimDateKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Period</b>
            {frozenColumns.indexOf('DimDateKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimDateKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimDateKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        frozen: frozenColumns.indexOf('DimDateKey') !== -1 ? true : false,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimDateKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimDateKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {dates &&
                  dates.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[2] + ' ' + cur[3]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimDateKey ? props.row.DimDateKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimDateKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {dates &&
                dates.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[2] + ' ' + cur[3]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimChartOfAccountsKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Account</b>
            {frozenColumns.indexOf('DimChartOfAccountsKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimChartOfAccountsKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimChartOfAccountsKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'DimChartOfAccountsKey',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimChartOfAccountsKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimChartOfAccountsKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimChartOfAccountsKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimChartOfAccountsKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {bsChartOfAccounts &&
                  bsChartOfAccounts.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimChartOfAccountsKey
                  ? props.row.DimChartOfAccountsKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimChartOfAccountsKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {bsChartOfAccounts &&
                bsChartOfAccounts.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'BalanceSheetCategory',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Category</b>
            {frozenColumns.indexOf('BalanceSheetCategory') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'BalanceSheetCategory')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'BalanceSheetCategory')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'BalanceSheetCategory',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'BalanceSheetCategory',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.BalanceSheetCategory}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    BalanceSheetCategory: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('BalanceSheetCategory') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'OpeningBalance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Opening Balance</b>
            {frozenColumns.indexOf('OpeningBalance') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'OpeningBalance')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'OpeningBalance')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'OpeningBalance')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'OpeningBalance',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.OpeningBalance}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    OpeningBalance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('OpeningBalance') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.OpeningBalance}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                OpeningBalance: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ClosingBalance',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Closing Balance</b>
            {frozenColumns.indexOf('ClosingBalance') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ClosingBalance')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ClosingBalance')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ClosingBalance')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ClosingBalance',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ClosingBalance}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ClosingBalance: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ClosingBalance') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ClosingBalance}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ClosingBalance: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'IsActive',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Active</b>
            {frozenColumns.indexOf('IsActive') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'IsActive')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        frozen: frozenColumns.indexOf('IsActive') !== -1 ? true : false,
        renderCell: (props) => (
          <div style={{ textAlign: 'center' }}>
            <Checkbox
              checked={props.row.IsActive}
              onChange={(e) => {
                props.onRowChange(
                  {
                    ...props.row,
                    IsActive: e.target.checked,
                  },
                  true,
                );
              }}
              color="success"
            />
          </div>
        ),
      },
    ];

    if (orgLength.length > 1) {
      balancesheet_columns.splice(1, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      balancesheet_columns.splice(1, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimCompanyKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimCompanyKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = balancesheet_columns.filter((col) => col.frozen === true);
    let unfrozenCols = balancesheet_columns.filter(
      (col) => col.frozen === false,
    );
    balancesheet_columns = [...frozenCols, ...unfrozenCols];

    return balancesheet_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    bsChartOfAccounts,
    dates,
    companies,
    organisations,
    orgLength,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.DimDateKey !== 'All'
          ? // eslint-disable-next-line
            r.DimDateKey == filters.DimDateKey
          : true) &&
        (filters.DimChartOfAccountsKey !== 'All'
          ? // eslint-disable-next-line
            r.DimChartOfAccountsKey == filters.DimChartOfAccountsKey
          : true) &&
        (filters.BalanceSheetCategory
          ? r.BalanceSheetCategory?.toLowerCase().includes(
              filters.BalanceSheetCategory.toLowerCase(),
            )
          : true) &&
        (filters.OpeningBalance
          ? r.OpeningBalance?.toString().includes(
              filters.OpeningBalance.toString(),
            )
          : true) &&
        (filters.ClosingBalance
          ? r.ClosingBalance?.toString().includes(
              filters.ClosingBalance.toString(),
            )
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportValue = useMemo(() => {
    let export_value = {
      Balancesheet: {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
      Date: {
        columns: [],
        data: [],
      },
      'General Ledger': {
        columns: [],
        data: [],
      },
    };

    export_value.Balancesheet.columns = [
      { title: 'DimCompanyKey', width: { wpx: 80 } },
      { title: 'DimOrganisationKey', width: { wpx: 80 } },
      { title: 'DimDateKey', width: { wpx: 80 } },
      { title: 'DimAccountName', width: { wpx: 80 } },
      { title: 'BalanceSheetCategory', width: { wpx: 80 } },
      { title: 'OpeningBalance', width: { wpx: 80 } },
      { title: 'ClosingBalance', width: { wpx: 80 } },
      { title: 'IsActive', width: { wpx: 80 } },
    ];
    tableRows.forEach((data) => {
      export_value.Balancesheet.data.push([
        {
          value: data.DimCompanyKey ? Number(data.DimCompanyKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimOrganisationKey ? Number(data.DimOrganisationKey) : -1,
          style: { numFmt: '0' },
        },
        {
          value: data.DimDateKey ? Number(data.DimDateKey) : -1,
          style: { numFmt: '0' },
        },
        { value: data.DimAccountName ? data.DimAccountName : '' },
        { value: data.BalanceSheetCategory ? data.BalanceSheetCategory : '' },
        {
          value: data.OpeningBalance ? Number(data.OpeningBalance) : 0,
          style: { numFmt: '0.00' },
        },
        {
          value: data.ClosingBalance ? Number(data.ClosingBalance) : 0,
          style: { numFmt: '0.00' },
        },
        { value: data.IsActive ? 'true' : 'false' },
      ]);
    });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    export_value.Date.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Date', width: { wpx: 80 } },
    ];
    dates &&
      dates.forEach((cur) => {
        export_value.Date.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[2] + ' ' + cur[3] },
        ]);
      });

    export_value['General Ledger'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    bsChartOfAccounts &&
      bsChartOfAccounts.forEach((cur) => {
        export_value['General Ledger'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] ? cur[1] : '' },
        ]);
      });

    return export_value;
  }, [tableRows, companies, organisations, dates, bsChartOfAccounts]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} />
        <ExportExcel filename="BalanceSheet" value={exportValue} />
        <IconButton onClick={() => onClickedAdd(name)}>
          <AddIcon />
        </IconButton>
        <IconButton onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={balanceSheetColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>
    </div>
  );
}

BalanceSheet.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  dates: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  chartofaccounts: PropTypes.array.isRequired,
  selectedDates: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
};

export default BalanceSheet;
